import { User } from "../types/entities/user"

const CHATWOOT_BASE = "https://chat.comfkino.ru"
const TOKEN_AUTH = "iweU63QwgtPzYNKUCaEAkA6U"
const TOKEN_GUEST = "9m9RhmL7P5CsGeDFu3LNUkFx"
const APP_VERSION = "1.0.0"
const EMBEDDED_STYLE_ID = "cw-embedded-styles"

let scriptLoadPromise: Promise<void> | null = null
let activeSessionKey: string | null = null
let embeddedContainer: HTMLElement | null = null
let embeddedLayoutObserver: ResizeObserver | null = null
let embeddedVisibilityObserver: MutationObserver | null = null
let pendingIdentifyParams: SupportChatParams | null = null
let chatwootEventsBound = false

export type SupportChatParams = {
    user: User | null
    deviceToken: string | null
    cityId: number
    cityName: string
    cinemaName: string
}

function getWebsiteToken(user: User | null) {
    return user?.id ? TOKEN_AUTH : TOKEN_GUEST
}

function getPhoneDigits(user: User | null) {
    const digits = (user?.phone || "").replace(/\D/g, "")
    if (digits.length === 10) return digits
    if (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))) return digits.slice(-10)
    return ""
}

function getChatwootIdentifier({ user, deviceToken }: Pick<SupportChatParams, "user" | "deviceToken">) {
    if (user?.chatwoot_identity?.identifier) return user.chatwoot_identity.identifier

    const phone10 = getPhoneDigits(user)
    if (phone10) return `8${phone10}`

    return deviceToken || "guest"
}

export function isChatwootIdentityReady(user: User | null | undefined) {
    if (!user?.id) return true
    return Boolean(user.chatwoot_identity?.identifier && user.chatwoot_identity.identifier_hash)
}

export async function refreshSupportUser(
    user: User | null,
    fetchUser: () => Promise<User>
): Promise<User | null> {
    if (!user?.id) return user
    try {
        return await fetchUser()
    } catch {
        return user
    }
}

function getContactName(user: User | null) {
    if (!user) return "Гость"
    const name = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(" ").trim()
    return name || "Гость"
}

function getPhoneE164(user: User | null) {
    const phone10 = getPhoneDigits(user)
    return phone10 ? `+7${phone10}` : undefined
}

function getUsableEmail(user: User | null) {
    const email = user?.email?.trim()
    if (!email || !email.includes("@") || email.length < 5) return undefined
    return email
}

function wait(ms: number) {
    return new Promise<void>(resolve => {
        window.setTimeout(resolve, ms)
    })
}

function bindChatwootEvents() {
    if (chatwootEventsBound) return
    chatwootEventsBound = true

    window.addEventListener("chatwoot:ready", () => {
        if (pendingIdentifyParams) applyChatwootUser(pendingIdentifyParams, true)
    })

    window.addEventListener("chatwoot:error", event => {
        console.warn("Chatwoot widget error", event)
    })
}

export function getChatwootSessionKey(params: SupportChatParams) {
    const websiteToken = getWebsiteToken(params.user)
    const identifier = getChatwootIdentifier(params)
    return `${websiteToken}:${identifier}:${params.cityId}`
}

function stopEmbeddedLayoutObserver() {
    embeddedLayoutObserver?.disconnect()
    embeddedLayoutObserver = null
}

function stopEmbeddedVisibilityObserver() {
    embeddedVisibilityObserver?.disconnect()
    embeddedVisibilityObserver = null
}

function keepEmbeddedWidgetVisible(holder: HTMLElement) {
    stopEmbeddedVisibilityObserver()

    const reveal = () => {
        if (embeddedContainer !== holder.parentElement) return

        holder.classList.remove("woot--hide")
        holder.style.removeProperty("display")
        syncEmbeddedWidgetLayout(holder)
    }

    reveal()
    embeddedVisibilityObserver = new MutationObserver(reveal)
    embeddedVisibilityObserver.observe(holder, { attributes: true, attributeFilter: ["class", "style"] })
}

function startEmbeddedLayoutObserver(holder: HTMLElement) {
    stopEmbeddedLayoutObserver()

    const sync = () => syncEmbeddedWidgetLayout(holder)
    sync()

    embeddedLayoutObserver = new ResizeObserver(sync)
    embeddedLayoutObserver.observe(holder)
    if (embeddedContainer) embeddedLayoutObserver.observe(embeddedContainer)

    window.setTimeout(sync, 0)
    window.setTimeout(sync, 250)
    window.setTimeout(sync, 1000)
}

function getChatwootFrame() {
    return document.getElementById("chatwoot_live_chat_widget") as HTMLIFrameElement | null
}

function hasChatwootFrame() {
    if (getChatwootFrame()) return true
    return Boolean(document.getElementById("cw-widget-holder")?.querySelector("iframe"))
}

function loadChatwootScript(): Promise<void> {
    if (window.chatwootSDK) return Promise.resolve()
    if (scriptLoadPromise) return scriptLoadPromise

    scriptLoadPromise = new Promise((resolve, reject) => {
        window.chatwootSettings = {
            hideMessageBubble: true,
            locale: "ru",
            darkMode: "dark",
            position: "right",
            showUnreadMessagesDialog: false
        }

        const onScriptLoad = () => resolve()
        const onScriptError = () => {
            scriptLoadPromise = null
            reject(new Error("Chatwoot SDK load failed"))
        }

        const existingScript = document.getElementById("chatwoot-sdk") as HTMLScriptElement | null
        if (existingScript) {
            if (window.chatwootSDK) {
                resolve()
                return
            }

            existingScript.addEventListener("load", onScriptLoad, { once: true })
            existingScript.addEventListener("error", onScriptError, { once: true })
            return
        }

        const script = document.createElement("script")
        script.id = "chatwoot-sdk"
        script.src = `${CHATWOOT_BASE}/packs/js/sdk.js`
        script.async = true
        script.onload = onScriptLoad
        script.onerror = onScriptError
        document.body.appendChild(script)
    })

    return scriptLoadPromise
}

function waitForChatwootReady(): Promise<void> {
    if (window.$chatwoot?.hasLoaded) return Promise.resolve()

    return new Promise((resolve, reject) => {
        const cleanup = () => {
            window.clearTimeout(timeoutId)
            window.clearInterval(pollId)
            window.removeEventListener("chatwoot:ready", onReady)
        }

        const finish = () => {
            cleanup()
            resolve()
        }

        const fail = (error: Error) => {
            cleanup()
            reject(error)
        }

        const onReady = () => finish()

        const timeoutId = window.setTimeout(() => {
            fail(new Error("Chatwoot widget init timeout"))
        }, 15000)

        const pollId = window.setInterval(() => {
            if (window.$chatwoot?.hasLoaded) finish()
        }, 100)

        window.addEventListener("chatwoot:ready", onReady)
    })
}

function waitForChatwootFrame(): Promise<void> {
    if (hasChatwootFrame()) return Promise.resolve()

    return new Promise((resolve, reject) => {
        const cleanup = () => {
            window.clearTimeout(timeoutId)
            window.clearInterval(pollId)
        }

        const finish = () => {
            cleanup()
            resolve()
        }

        const fail = (error: Error) => {
            cleanup()
            reject(error)
        }

        const timeoutId = window.setTimeout(() => {
            fail(new Error("Chatwoot iframe init timeout"))
        }, 15000)

        const pollId = window.setInterval(() => {
            if (hasChatwootFrame()) finish()
        }, 100)
    })
}

function applyEmbeddedStyles() {
    if (document.getElementById(EMBEDDED_STYLE_ID)) return

    const style = document.createElement("style")
    style.id = EMBEDDED_STYLE_ID
    style.textContent = `
        .profile-support-chat-embed {
            position: relative;
            display: flex;
            width: 100%;
        }

        .profile-support-chat-embed #cw-widget-holder,
        .profile-support-chat-embed .woot-widget-holder {
            position: relative !important;
            inset: 0 !important;
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            min-height: 100% !important;
            max-height: none !important;
            margin: 0 !important;
            transform: none !important;
            opacity: 1 !important;
            visibility: visible !important;
            box-shadow: none !important;
            border-radius: 14px !important;
            overflow: hidden !important;
            flex: 1 1 auto;
        }

        .profile-support-chat-embed #cw-widget-holder.woot--hide,
        .profile-support-chat-embed .woot-widget-holder.woot--hide {
            transform: none !important;
            opacity: 1 !important;
            visibility: visible !important;
            bottom: 0 !important;
        }

        .profile-support-chat-embed #cw-bubble-holder,
        .profile-support-chat-embed .woot--bubble-holder {
            display: none !important;
        }

        .profile-support-chat-embed #chatwoot_live_chat_widget {
            width: 100% !important;
            height: 100% !important;
            min-height: 100% !important;
            max-height: none !important;
        }
    `
    document.head.appendChild(style)
}

function syncEmbeddedWidgetLayout(holder: HTMLElement) {
    const iframe = getChatwootFrame()

    holder.style.setProperty("position", "relative", "important")
    holder.style.setProperty("inset", "0", "important")
    holder.style.setProperty("width", "100%", "important")
    holder.style.setProperty("height", "100%", "important")
    holder.style.setProperty("min-height", "100%", "important")
    holder.style.setProperty("max-height", "none", "important")
    holder.style.setProperty("transform", "none", "important")

    if (iframe) {
        iframe.style.setProperty("width", "100%", "important")
        iframe.style.setProperty("height", "100%", "important")
        iframe.style.setProperty("min-height", "100%", "important")
        iframe.style.setProperty("max-height", "none", "important")
    }
}

function destroyChatwootWidget() {
    stopEmbeddedLayoutObserver()
    stopEmbeddedVisibilityObserver()

    embeddedContainer?.classList.remove("profile-support-chat-embed")
    embeddedContainer = null

    try {
        window.$chatwoot?.reset()
    } catch {
        // reset may fail if widget is already torn down
    }

    document.getElementById("cw-widget-holder")?.remove()
    document.getElementById("cw-bubble-holder")?.remove()
    getChatwootFrame()?.remove()

    delete window.$chatwoot
    activeSessionKey = null
    pendingIdentifyParams = null
}

export function resetSupportChat() {
    destroyChatwootWidget()
}

function buildContactAttributes(params: SupportChatParams) {
    const { user, cityName, cinemaName } = params
    const identifierHash = user?.chatwoot_identity?.identifier_hash?.trim() || ""
    const attributes: Record<string, string> = {
        cityName,
        cinemaName,
        userPhone: user?.phone || "",
        device_model: navigator.userAgent,
        os: "web",
        app_version: APP_VERSION,
        identity_ready: identifierHash ? "yes" : "no"
    }
    if (user?.id) attributes.user_id = String(user.id)
    return attributes
}

function applyChatwootUser(params: SupportChatParams, allowWithoutFrame = false) {
    if (!window.$chatwoot) return
    if (!allowWithoutFrame && !hasChatwootFrame()) return

    const { user, deviceToken } = params
    const identifier = getChatwootIdentifier({ user, deviceToken })
    const identifierHash = user?.chatwoot_identity?.identifier_hash?.trim() || ""
    const attributes = buildContactAttributes(params)
    const userPayload: ChatwootUser = {
        name: getContactName(user),
        custom_attributes: attributes
    }

    const email = getUsableEmail(user)
    if (email) userPayload.email = email

    const phoneNumber = getPhoneE164(user)
    if (phoneNumber) userPayload.phone_number = phoneNumber

    if (user?.image) userPayload.avatar_url = user.image
    if (identifierHash) userPayload.identifier_hash = identifierHash

    // User-инбокс с hmac_mandatory отвергает identify без хеша.
    if (!user?.id || identifierHash) {
        window.$chatwoot.setUser(identifier, userPayload)
    }

    window.$chatwoot.setCustomAttributes(attributes)
    window.$chatwoot.setConversationCustomAttributes?.({
        cityName: params.cityName,
        cinemaName: params.cinemaName
    })
}

async function identifyChatwootUser(params: SupportChatParams) {
    pendingIdentifyParams = params
    if (!hasChatwootFrame()) {
        await waitForChatwootFrame()
    }
    applyChatwootUser(params)
    await wait(500)
    applyChatwootUser(params)
}

async function createChatwootWidget(params: SupportChatParams) {
    const sessionKey = getChatwootSessionKey(params)
    const websiteToken = getWebsiteToken(params.user)

    await loadChatwootScript()
    bindChatwootEvents()

    if (!window.chatwootSDK) {
        throw new Error("Chatwoot SDK is not available")
    }

    if (activeSessionKey && activeSessionKey !== sessionKey) {
        destroyChatwootWidget()
    }

    if (window.$chatwoot && activeSessionKey === sessionKey) {
        if (!window.$chatwoot.hasLoaded) {
            await waitForChatwootReady()
        }
        await waitForChatwootFrame()
        return
    }

    window.chatwootSDK.run({
        websiteToken,
        baseUrl: CHATWOOT_BASE
    })

    activeSessionKey = sessionKey
    await waitForChatwootReady()
    await waitForChatwootFrame()

    if (!window.$chatwoot) {
        throw new Error("Chatwoot widget is not available")
    }
}

function mountWidgetToContainer(container: HTMLElement) {
    const holder = document.getElementById("cw-widget-holder")
    if (!holder) {
        throw new Error("Chatwoot widget holder is not available")
    }

    container.classList.add("profile-support-chat-embed")
    container.appendChild(holder)
    embeddedContainer = container

    holder.classList.remove("woot--hide")
    holder.style.removeProperty("display")
    syncEmbeddedWidgetLayout(holder)
}

function openEmbeddedWidget(holder: HTMLElement) {
    holder.classList.remove("woot--hide")
    holder.style.removeProperty("display")

    const iframe = holder.querySelector("iframe") as HTMLIFrameElement | null
    if (iframe) {
        iframe.style.setProperty("visibility", "visible", "important")
    }

    if (window.$chatwoot && !window.$chatwoot.isOpen) {
        window.$chatwoot.toggle("open")
    }

    syncEmbeddedWidgetLayout(holder)
    keepEmbeddedWidgetVisible(holder)
}

export async function openSupportChat(params: SupportChatParams) {
    await createChatwootWidget(params)
    await identifyChatwootUser(params)
    window.$chatwoot?.toggle("open")
}

export function closeSupportChat() {
    if (!hasChatwootFrame()) return
    window.$chatwoot?.toggle("close")
}

export async function mountEmbeddedSupportChat(
    container: HTMLElement,
    params: SupportChatParams,
    isActive: () => boolean = () => true
) {
    applyEmbeddedStyles()
    pendingIdentifyParams = params
    await createChatwootWidget(params)
    if (!isActive()) return

    mountWidgetToContainer(container)
    if (!isActive()) {
        resetSupportChat()
        return
    }

    await waitForChatwootFrame()
    if (!window.$chatwoot?.hasLoaded) {
        await waitForChatwootReady()
    }
    if (!isActive()) return

    await identifyChatwootUser(params)
    if (!isActive()) return

    const holder = document.getElementById("cw-widget-holder")
    if (!holder) {
        throw new Error("Chatwoot widget holder is not available")
    }

    openEmbeddedWidget(holder)
    startEmbeddedLayoutObserver(holder)
}

export function unmountEmbeddedSupportChat() {
    resetSupportChat()
}
